import React from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../pages/PageLayout';
import '../styles/LegalPages.css';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <PageLayout>
      <div className="legal-page-container">
        <div className="legal-content">
          <h1 className="legal-title">Privacy Policy</h1>
          <p className="legal-updated">
            <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <section className="legal-section">
            <h2 className="section-title">1. Introduction</h2>
            <p>
              Welcome to AllSportsHubs ("we," "our," or "us"). We are committed to protecting your personal 
              information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, 
              and safeguard your information when you use our sports scheduling platform.
            </p>
            <p>
              By using AllSportsHubs, you agree to the collection and use of information in accordance with 
              this policy. If you do not agree with our policies and practices, please do not use our service.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">2. Information We Collect</h2>
            
            <h3 className="subsection-title">2.1 Personal Information</h3>
            <p>We collect information that you provide directly to us, including:</p>
            <ul className="legal-list">
              <li>Name and email address (when you create an account)</li>
              <li>Account credentials (username and encrypted password)</li>
              <li>Profile information you choose to provide</li>
              <li>Sports venue information (addresses, locations)</li>
              <li>Team and schedule data you create</li>
            </ul>

            <h3 className="subsection-title">2.2 Automatically Collected Information</h3>
            <p>When you use our service, we automatically collect certain information, including:</p>
            <ul className="legal-list">
              <li>Device information (browser type, operating system)</li>
              <li>Usage data (pages visited, features used)</li>
              <li>IP address and location data</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>

            <h3 className="subsection-title">2.3 Third-Party Authentication</h3>
            <p>
              If you sign in using Google OAuth, we receive basic profile information from Google 
              including your name and email address, as permitted by your Google account settings.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul className="legal-list">
              <li>Provide, maintain, and improve our sports scheduling services</li>
              <li>Create and manage your account</li>
              <li>Process your schedules, venues, and team data</li>
              <li>Send you technical notices, updates, and support messages</li>
              <li>Respond to your comments, questions, and customer service requests</li>
              <li>Monitor and analyze trends, usage, and activities</li>
              <li>Detect, prevent, and address technical issues and security threats</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="section-title">4. Information Sharing and Disclosure</h2>
            <p>We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:</p>
            <ul className="legal-list">
              <li><strong>With your consent:</strong> We may share your information when you give us permission</li>
              <li><strong>Service providers:</strong> We use Supabase for database hosting and authentication services</li>
              <li><strong>Legal requirements:</strong> We may disclose information if required by law or to protect our rights</li>
              <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="section-title">5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational security measures to protect your personal 
              information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="legal-list">
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication using industry-standard protocols</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and authentication requirements</li>
            </ul>
            <p>
              However, no method of transmission over the internet or electronic storage is 100% secure. 
              While we strive to use commercially acceptable means to protect your information, we cannot 
              guarantee absolute security.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">6. Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to provide our services and fulfill 
              the purposes outlined in this Privacy Policy. When you delete your account, we will delete or 
              anonymize your personal information, except where we are required to retain it for legal or 
              legitimate business purposes.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">7. Your Privacy Rights</h2>
            <p>Depending on your location, you may have the following rights:</p>
            <ul className="legal-list">
              <li><strong>Access:</strong> Request access to your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Data portability:</strong> Request a copy of your data in a portable format</li>
              <li><strong>Opt-out:</strong> Opt out of certain data processing activities</li>
            </ul>
            <p>
              To exercise these rights, please contact us through your account settings or via our support channels.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">8. Cookies and Tracking Technologies</h2>
            <p>
              We use cookies and similar tracking technologies to track activity on our service and hold certain 
              information. Cookies are files with small amounts of data that are stored on your device. You can 
              instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, 
              if you do not accept cookies, you may not be able to use some portions of our service.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">9. Third-Party Services</h2>
            <p>Our service integrates with third-party services including:</p>
            <ul className="legal-list">
              <li><strong>Supabase:</strong> Database and authentication services</li>
              <li><strong>Google OAuth:</strong> Optional authentication method</li>
              <li><strong>Google AdSense:</strong> Advertising services</li>
            </ul>
            <p>
              These third-party services have their own privacy policies governing their use of your information. 
              We encourage you to review their privacy policies.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">10. Children's Privacy</h2>
            <p>
              Our service is not intended for children under 13 years of age. We do not knowingly collect personal 
              information from children under 13. If you are a parent or guardian and believe your child has 
              provided us with personal information, please contact us so we can delete such information.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">11. International Data Transfers</h2>
            <p>
              Your information may be transferred to and maintained on servers located outside of your state, 
              province, country, or other governmental jurisdiction where data protection laws may differ. 
              By using our service, you consent to the transfer of your information to these locations.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">12. Changes to This Privacy Policy</h2>
            <p>
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting 
              the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to 
              review this Privacy Policy periodically for any changes. Changes are effective when posted on this page.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">13. Contact Us</h2>
            <p>
              If you have questions or concerns about this Privacy Policy or our privacy practices, please 
              contact us through your account settings or visit our support page.
            </p>
          </section>

          <div className="legal-footer">
            <button
              className="legal-back-btn"
              onClick={() => navigate(-1)}
            >
              ← Go Back
            </button>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default PrivacyPolicy;